import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { headRewrite } from "../function/headRewrite";
import { Helmet } from "react-helmet-async";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
    [theme.breakpoints.down("sm")]: {
      paddingBottom: 86,
    },
  },
  pageFrame: {
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      borderRight: "none",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #111",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 0),
    },
  },
  logo: {
    fontWeight: 700,
    letterSpacing: "-0.04em",
    fontSize: 28,
    [theme.breakpoints.down("sm")]: {
      fontSize: 24,
    },
  },
  nav: {
    display: "flex",
    gap: theme.spacing(4),
    fontSize: 13,
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(2),
      fontSize: 12,
    },
  },
  navLink: {
    color: "#111",
    textDecoration: "none",
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(26px, 4.5vw, 46px)",
    lineHeight: 1.16,
    letterSpacing: "-0.08em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heroBody: {
    maxWidth: 560,
    fontSize: 15,
    lineHeight: 2,
    color: "#222",
    marginBottom: theme.spacing(4),
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  ctaButton: {
    minWidth: 250,
    minHeight: 52,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: "auto",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#111",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
  ctaNote: {
    fontSize: 13,
    lineHeight: 1.9,
    color: "#333",
    marginTop: theme.spacing(2.5),
    marginBottom: 0,
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  heroImageWrap: {
    position: "relative",
    minHeight: 520,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 360,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 20,
    width: 260,
    height: 260,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  coatMock: {
    position: "relative",
    width: "70%",
    maxWidth: 360,
    background: "transparent",
    boxShadow: "none",
    transform: "rotate(2deg)",
   
    [theme.breakpoints.down("sm")]: {
      maxWidth: 280,
    },
  },
  coatButton: {
    position: "absolute",
    left: "54%",
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "#050505",
    border: "1px solid #555",
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  card: {
    border: "1px solid #111",
    minHeight: 250,
    height: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  arrowCircle: {
    alignSelf: "flex-end",
    width: 34,
    height: 34,
    border: "1px solid #111",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  comparisonBlock: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  comparisonItem: {
    borderLeft: "1px solid #111",
    paddingLeft: theme.spacing(3),
  },
  bigLetter: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 58,
    lineHeight: 1,
    marginBottom: theme.spacing(1),
  },
  brandGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  brandItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.2, 1.5),
    textAlign: "center",
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 16,
  },
  useCaseGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  useCaseItem: {
    minHeight: 160,
    border: "1px solid #111",
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
  },
  recommendedGrid: {
    display: "flex",
    gap: theme.spacing(2),
    overflowX: "auto",
    paddingBottom: theme.spacing(1.5),
    WebkitOverflowScrolling: "touch",
    scrollSnapType: "x proximity",
  },
  recommendedCard: {
    display: "block",
    flex: "0 0 280px",
    color: "#111",
    textDecoration: "none",
    border: "1px solid #111",
    background: "transparent",
    minHeight: "100%",
    scrollSnapAlign: "start",
    [theme.breakpoints.down("xs")]: {
      flexBasis: "76vw",
    },
  },
  recommendedImageWrap: {
    aspectRatio: "1 / 1",
    background: "#e4e4e4",
    overflow: "hidden",
  },
  recommendedImage: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  recommendedBody: {
    padding: theme.spacing(2),
  },
  recommendedMeta: {
    fontSize: 11,
    letterSpacing: "0.1em",
    marginBottom: theme.spacing(1),
    color: "#555",
    textTransform: "uppercase",
  },
  faqGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  faqItem: {
    border: "1px solid #111",
    padding: theme.spacing(2.5),
    background: "transparent",
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  faqAnswer: {
    fontSize: 14,
    lineHeight: 1.9,
    color: "#222",
    whiteSpace: "pre-line",
  },
  archiveImages: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1.35fr",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  imagePanel: {
    height: 220,
    background: "#eeeeee",
    position: "relative",
    overflow: "hidden",
  },
  detailPanel: {
    height: 200,
    background: "#eeeeee",
    color: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 12,
    letterSpacing: "0.06em",
    lineHeight: 1.8,
  },
  image:{
    display: "block",
    width: "100%",
    height: "auto",
    objectFit: "contain",
  },
  imageCaption: {
    fontSize: 10,
    letterSpacing: "0.12em",
    marginTop: theme.spacing(1),
    textTransform: "uppercase",
  },
  timeline: {
    position: "relative",
    paddingLeft: theme.spacing(5),
    "&:before": {
      content: '""',
      position: "absolute",
      left: 9,
      top: 8,
      bottom: 8,
      width: 1,
      background: "#111",
    },
  },
  timelineItem: {
    position: "relative",
    paddingBottom: theme.spacing(4),
    borderBottom: "1px dashed #aaa",
    marginBottom: theme.spacing(3),
    "&:before": {
      content: '""',
      position: "absolute",
      left: -42,
      top: 4,
      width: 16,
      height: 16,
      border: "1px solid #111",
      borderRadius: "50%",
      background: "#eeeeee",
    },
  },
  timelineNumber: {
    fontSize: 24,
    marginRight: theme.spacing(3),
  },
  philosophyGraphic: {
    position: "relative",
    minHeight: 180,
    [theme.breakpoints.down("sm")]: {
      minHeight: 120,
    },
  },
  circle: {
    position: "absolute",
    right: 78,
    top: 20,
    width: 120,
    height: 120,
    border: "1px solid #111",
    borderRadius: "50%",
  },
  square: {
    position: "absolute",
    right: 0,
    top: 46,
    width: 120,
    height: 120,
    border: "1px solid #111",
  },
  blackDot: {
    position: "absolute",
    right: 82,
    top: 100,
    width: 20,
    height: 20,
    background: "#111",
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  fixedCta: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 3200,
    display: "none",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.4, 2),
    borderTop: "1px solid #111",
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
    },
  },
  fixedCtaText: {
    flex: 1,
    minWidth: 0,
    fontSize: 12,
    lineHeight: 1.55,
    color: "#222",
  },
  fixedCtaButton: {
    flex: "0 0 auto",
    minWidth: 154,
    minHeight: 46,
    borderRadius: 0,
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
}));

const brands = [
  "COMME des GARÇONS",
  "Yohji Yamamoto",
  "JUNYA WATANABE",
  "NOIR KEI NINOMIYA",
  "Maison Margiela",
  "Raf Simons",
  "sacai",
  "ISSEY MIYAKE",
  "PRADA",
  "BALENCIAGA",
  "Rick Owens",
  "UNDERCOVER",
];

const lineUrl = "https://line.me/R/ti/p/@240yzqkh";

const useCases = [
  {
    number: "01",
    title: "購入前に試す",
    text: "高額なアーカイブを購入する前に、サイズ感や自分との相性を確認できます。",
  },
  {
    number: "02",
    title: "撮影・作品撮り",
    text: "ルック撮影、ポートレート、作品制作など、服の強さが必要な場面で使えます。",
  },
  {
    number: "03",
    title: "イベント・展示会・デート",
    text: "いつもとは違う一着を着たい日にも、購入以外の選択肢を持てます。",
  },
  {
    number: "04",
    title: "旅行・ライブの日に",
    text: "旅先やライブなど、思い出に残る日に合わせて特別な服を選べます。",
  },
  {
    number: "05",
    title: "服飾研究・資料",
    text: "写真だけでは分からない構造、素材、シルエットを実物で確認できます。",
  },
  {
    number: "06",
    title: "所有せずに楽しむ",
    text: "一着を購入する予算で複数のブランドやシーズンを体験。保管や売却の手間もありません。",
  },
];

const recommendedItems = [
  {
    meta: "COMME des GARCONS / 2023AW",
    title: "購入前に試したいアーカイブ",
    href: "https://www.enclopedia-official.com/product/a2a6a5a4-af3f-48b3-94e1-d14952e4cf59",
    image: "media/product/1782891635.6017342a2a6a5a4-af3f-48b3-94e1-d14952e4cf59.png",
  },
  {
    meta: "COMME des GARCONS HOMME PLUS / 2025SS",
    title: "シルエットを確かめたい一着",
    href: "https://www.enclopedia-official.com/product/d5827138-e1cd-43bf-9660-8fde24e4df18",
    image: "media/product/1758033394.7737393d5827138-e1cd-43bf-9660-8fde24e4df18.jpg",
  },
  {
    meta: "sacai x WTAPS / 2025SS",
    title: "コラボレーションジャケット",
    href: "https://www.enclopedia-official.com/product/079f1c35-7f8d-45e4-aca8-fb7da822e05b",
    image: "media/product/1773151184.3163898079f1c35-7f8d-45e4-aca8-fb7da822e05b.png",
  },
  {
    meta: "RAF SIMONS / 2022-2023AW",
    title: "ボンバージャケット",
    href: "https://www.enclopedia-official.com/product/c1306d6c-6029-4aaa-a316-9d8047c79695",
    image: "media/product/1771860586.6694794c1306d6c-6029-4aaa-a316-9d8047c79695.jpg",
  },
  {
    meta: "JUNYA WATANABE MAN / 2025SS",
    title: "パッチワークテーラードジャケット",
    href: "https://www.enclopedia-official.com/product/d6b727ef-ac18-43d9-8323-0b8afc855db7",
    image: "media/product/1777819179.8824942d6b727ef-ac18-43d9-8323-0b8afc855db7.png",
  },
  {
    meta: "NOIR KEI NINOMIYA / 2025SS",
    title: "デザインテーラードジャケット",
    href: "https://www.enclopedia-official.com/product/30dc8d80-a884-4515-b7fd-fa6a19c1e457",
    image: "media/product/1772806139.085684530dc8d80-a884-4515-b7fd-fa6a19c1e457.jpg",
  },
];

const faqItems = [
  {
    question: "レンタル期間は何日ですか？",
    answer: "受取日と返却日を含めて合計12日間です。実際に着用できる期間は、商品受取日と返却日を除いた10日間です。",
  },
  {
    question: "返却方法は？",
    answer: "商品に同梱されている返却用袋に、購入したアイテムを除く返却対象アイテムを入れ、案内に沿ってコンビニまたはヤマト運輸から返送できます。",
  },
  {
    question: "汚れた場合は？",
    answer: "通常の着用範囲内の汚れであれば、クリーニング・修繕費用はレンタル料金に含まれます。大きな破損や通常使用を超える汚損がある場合は、状態に応じて確認します。",
  },
  {
    question: "サイズが不安な場合は？",
    answer: "商品ページの寸法をご確認ください。着用感や合わせ方に不安がある場合は、LINEで事前に相談できます。",
  },
  {
    question: "レンタル後に購入できますか？",
    answer: "はい。レンタル中のアイテムは、やることリストまたは予約関連画面から購入を選べる場合があります。",
  },
  {
    question: "購入する場合、レンタル料は差し引かれますか？",
    answer: "表示されている購入価格から、レンタル料と返却手数料を差し引いた金額で購入できます。",
  },
  {
    question: "LINEで相談できますか？",
    answer: "はい。サイズ感、着用シーン、合わせ方など、レンタル前にLINEで相談できます。",
  },
];

function getUtmParams() {
  if (typeof window === "undefined") {
    return {
      source: "direct",
      medium: "",
      campaign: "",
    };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get("utm_source") || "direct",
    medium: params.get("utm_medium") || "",
    campaign: params.get("utm_campaign") || "",
  };
}

function trackArchiveLpEvent(eventName, payload = {}) {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("event", eventName, {
    page: "archive_fashion_rental",
    ...getUtmParams(),
    ...payload,
  });
}

function buildImageUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${process.env.REACT_APP_IMAGE_URL || ""}${path}`;
}

export default function ArchiveFashionRentalScreen() {
  const classes = useStyles();
  const title =
    "アーカイブファッションレンタルサービス｜憧れのブランドを気軽に体験｜Enclopedia";
  const description =
    "Enclopediaは、COMME des GARÇONS、sacai、Yohji Yamamoto、Maison Margielaなど、多くのブランドの服をレンタルで体験できるサービスです。高額で希少なアーカイブファッションに袖を通し、サイズ感・素材感・シルエットを実物で確認できます。";
  const keyword =
    "アーカイブファッションレンタル,アーカイブファッション,ブランド古着レンタル,デザイナーズブランドレンタル,ヴィンテージブランドレンタル,ファッションレンタル,enclopedia,アンクロペディア,買う前に着る,買う前に試せる,コムデギャルソンレンタル,ヨウジヤマモトレンタル,マルジェラレンタル";
  const canonicalUrl =
    `${typeof window !== "undefined" ? window.location.origin : ""}/archive_fashion_rental`;
  const ogImage =
    process.env.REACT_APP_IMAGE_URL
      ? `${process.env.REACT_APP_IMAGE_URL}media/archive_fashion/archive_fashion1.png`
      : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(title, description, keyword);
    trackArchiveLpEvent("archive_lp_view");
  }, [title, description, keyword]);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keyword} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Enclopedia" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: title,
            description,
            url: canonicalUrl,
            isPartOf: {
              "@type": "WebSite",
              name: "Enclopedia",
              url: typeof window !== "undefined" ? window.location.origin : "",
            },
            about: [
              "アーカイブファッション",
              "ブランド古着レンタル",
              "デザイナーズブランドレンタル",
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: typeof window !== "undefined" ? window.location.origin : "",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "アーカイブファッションレンタル",
                item: canonicalUrl,
              },
            ],
          })}
        </script>
      </Helmet>
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow} style={{marginLeft:'7px'}}>ARCHIVE FASHION RENTAL</div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  着てみたい服を、
                  <br />
                  遠い存在にしない。
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  アーカイブとしての希少性、実物を体験する機会の少なさ、高額な価格。
                  デザイン性の高い服ほど、購入や着回しに迷いやすいものです。
                  Enclopediaは、COMME des GARÇONS、sacai、Yohji Yamamoto、Maison
                  Margielaなど、多くのブランドの服にレンタルで袖を通せるサービスです。
                  購入前にサイズ感や着こなしを確かめたり、旅行・ライブ・展示会などの特別な日に着たり。
                  一着を所有する代わりに、複数のブランドやシーズンを体験できます。
                </Typography>
                <div className={classes.buttonRow}>
                  <Button
                    className={`${classes.primaryButton} ${classes.ctaButton}`}
                    component="a"
                    href="https://www.enclopedia-official.com/"
                    onClick={() =>
                      trackArchiveLpEvent("archive_lp_product_list_click", {
                        position: "hero",
                        destination: "home_product_list",
                      })
                    }
                  >
                    レンタルできる商品を見る
                  </Button>
                  <Button
                    className={`${classes.secondaryButton} ${classes.ctaButton}`}
                    component="a"
                    href="/register?source=archive_fashion_rental"
                    onClick={() =>
                      trackArchiveLpEvent("archive_lp_register_click", {
                        position: "hero",
                        destination: "register",
                      })
                    }
                  >
                    無料登録してレンタル予約へ
                  </Button>
                </div>
                <Typography component="p" className={classes.ctaNote}>
                  気になる商品を見つけたら、商品詳細を見るか、無料登録してレンタル予約へ進めます。登録料はかかりません。
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.heroImageWrap}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroNumber}>01 / EXPERIENCE</div>
                  <div className={classes.coatMock}>
                    <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion1.png"} className={classes.image} alt={'Enclopediaでレンタルできるアーカイブファッションの着用イメージ'}/>
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={5}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>02 / WHY RENT?</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  見るだけで終わらせない。
                </Typography>
                <Typography component="p" className={classes.body}>
                {"ハイファッションやアーカイブファッションは、いつの間にか遠い存在になっています。\n\n本当は着てみたい服でも、購入するには勇気がいる。実物に触れる機会も少ない。\nだからこそEnclopediaは、そうした服をただ眺める対象ではなく、実際に袖を通せる体験として開いていきます。\n\n所有する前に、まず着てみる。買えないから諦めるのではなく、レンタルで体験する。\n身体に乗せたとき、はじめて自分との距離が分かります。"}
                </Typography>
                <div className={classes.keywordLine}>
                  <span>material</span>
                  <span>/</span>
                  <span>silhouette</span>
                  <span>/</span>
                  <span>proportion</span>
                  <span>/</span>
                  <span>time</span>
                </div>
              </Grid>

              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>03 / USE CASE</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  こんな時に借りる。
                </Typography>
                <div className={classes.useCaseGrid}>
                  {useCases.map((item) => (
                    <div className={classes.useCaseItem} key={item.number}>
                      <div>
                        <div className={classes.cardNumber}>{item.number}</div>
                        <div className={classes.cardTitle}>{item.title}</div>
                      </div>
                      <Typography className={classes.body} component="p">
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>04 / WEAR FIRST</span>
              <span className={classes.labelLine} />
            </div>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <div className={classes.card}>
                  <div>
                    <div className={classes.cardNumber}>01</div>
                    <div className={classes.cardTitle}>RENT</div>
                    <div className={classes.smallRule} />
                    <Typography className={classes.body} component="p">
                      気になるアーカイブをレンタルする
                    </Typography>
                  </div>
                  <div className={classes.arrowCircle}>→</div>
                </div>
              </Grid>
              <Grid item xs={12} sm={4}>
                <div className={classes.card}>
                  <div>
                    <div className={classes.cardNumber}>02</div>
                    <div className={classes.cardTitle}>WEAR</div>
                    <div className={classes.smallRule} />
                    <Typography className={classes.body} component="p">
                      実際に着て、サイズ感や雰囲気を確かめる
                    </Typography>
                  </div>
                  <div className={classes.arrowCircle}>→</div>
                </div>
              </Grid>
              <Grid item xs={12} sm={4}>
                <div className={classes.card}>
                  <div>
                    <div className={classes.cardNumber}>03</div>
                    <div className={classes.cardTitle}>BUY OR RETURN</div>
                    <div className={classes.smallRule} />
                    <Typography className={classes.body} component="p">
                      気に入れば購入。合わなければ返却。
                    </Typography>
                  </div>
                  <div className={classes.arrowCircle}>→</div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>05 / RECOMMENDED ITEMS</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              購入前に試したいアーカイブ
            </Typography>
            <div className={classes.recommendedGrid}>
              {recommendedItems.map((item) => (
                <a
                  href={item.href}
                  className={classes.recommendedCard}
                  key={item.href}
                  onClick={() =>
                    trackArchiveLpEvent("archive_lp_recommended_item_click", {
                      position: "recommended_items",
                      destination: item.href,
                      item_name: item.title,
                    })
                  }
                >
                  <div className={classes.recommendedImageWrap}>
                    <img
                      src={buildImageUrl(item.image)}
                      className={classes.recommendedImage}
                      alt={item.title}
                    />
                  </div>
                  <div className={classes.recommendedBody}>
                    <div className={classes.recommendedMeta}>{item.meta}</div>
                    <Typography component="h3" className={classes.cardTitle}>
                      {item.title}
                    </Typography>
                  </div>
                </a>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>06 / COMPARISON</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  ブランド古着通販との違い
                </Typography>
                <div className={classes.comparisonBlock}>
                  <div className={classes.comparisonItem}>
                    <div className={classes.bigLetter}>A</div>
                    <div className={classes.cardTitle}>BUY FIRST</div>
                    <Typography component="p" className={classes.body}>
                      従来のブランド古着通販は、写真と説明文、寸法だけで購入を判断する仕組みです。
                    </Typography>
                  </div>
                  <div className={classes.comparisonItem}>
                    <div className={classes.bigLetter}>B</div>
                    <div className={classes.cardTitle}>WEAR FIRST</div>
                    <Typography component="p" className={classes.body}>
                      Enclopediaは、実際に着てから判断できます。購入しない場合も、保管や売却を気にせず返却できます。
                    </Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>07 / BRANDS</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  取り扱いブランド
                </Typography>
                <Typography component="p" className={classes.body}>
                  一着を買う予算で、複数のブランドやシーズンを少しずつ体験できます。
                </Typography>
                <div className={classes.brandGrid}>
                  {brands.map((brand) => (
                    <div className={classes.brandItem} key={brand}>
                      {brand}
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>08 / ARCHIVE DETAIL</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  服を、資料として見る。
                  <br />
                  そして、実際に着る。
                </Typography>
                <div className={classes.archiveImages}>
                  <div>
                    <div className={classes.imagePanel}>
                      <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion2.png"} className={classes.image} alt={'アーカイブファッションのフロントディテール'}/>
                    </div>
                    <div className={classes.imageCaption}>FRONT</div>
                  </div>
                  <div>
                    <div className={classes.imagePanel}>
                      <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion3.png"} className={classes.image} alt={'アーカイブファッションのバックディテール'}/>
                    </div>
                    <div className={classes.imageCaption}>BACK</div>
                  </div>
                  <div>
                    <div className={classes.detailPanel}>
                      <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion4.png"} className={classes.image} alt={'ブランドアーカイブのラベルディテール'}/>
                    </div>
                    <div className={classes.imageCaption}>LABEL</div>
                    <div style={{ height: 16 }} />
                    <div className={classes.detailPanel}>
                      <img src={process.env.REACT_APP_IMAGE_URL + "media/archive_fashion/archive_fashion5.png"} className={classes.image} alt={'アーカイブファッションの素材とディテール'}/>
                    </div>
                    <div className={classes.imageCaption}>FABRIC / DETAIL</div>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>09 / HOW IT WORKS</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  利用の流れ
                </Typography>
                <div className={classes.timeline}>
                  {[
                    ["01", "アイテムを選ぶ", "気になるアーカイブをラインナップから選びます。"],
                    ["02", "レンタルする", "レンタル期間とプランを選び、注文します。"],
                    ["03", "実際に着る", "実際に着て、サイズ感や雰囲気をじっくり確かめます。"],
                    ["04", "返却または購入", "気に入れば購入。合わなければ返却できます。"],
                  ].map(([num, titleText, text]) => (
                    <div className={classes.timelineItem} key={num}>
                      <Grid container alignItems="flex-start" spacing={2}>
                        <Grid item>
                          <div className={classes.timelineNumber}>{num}</div>
                        </Grid>
                        <Grid item xs>
                          <Typography className={classes.cardTitle} component="h3">
                            {titleText}
                          </Typography>
                          <Typography className={classes.body} component="p">
                            {text}
                          </Typography>
                        </Grid>
                      </Grid>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>10 / FAQ</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              レンタル前に気になること。
            </Typography>
            <div className={classes.faqGrid}>
              {faqItems.map((item) => (
                <div className={classes.faqItem} key={item.question}>
                  <Typography component="h3" className={classes.faqQuestion}>
                    {item.question}
                  </Typography>
                  <Typography component="p" className={classes.faqAnswer}>
                    {item.answer}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>11 / PHILOSOPHY</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  アーカイブファッションを、
                  <br />
                  一部の人だけのものにしない。
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography component="p" className={classes.body}>
                  {"アーカイブファッションは、本来もっと多くの人に開かれるべき文化です。\nEnclopediaは、所有を目的とするのではなく、体験し、理解し、未来へつなぐための循環をつくります。\n服が持つ背景や文脈を尊重しながら、次の世代に受け継いでいく。それが、私たちの使命です。"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <div className={classes.philosophyGraphic}>
                  <div className={classes.circle} />
                  <div className={classes.square} />
                  <div className={classes.blackDot} />
                </div>
              </Grid>
            </Grid>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="h2" className={classes.finalTitle}>
              アーカイブファッションを、買う前に着る。
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              希少なブランドアーカイブをレンタル・購入できます。
            </Typography>
            <div className={classes.buttonRow} style={{ justifyContent: "center" }}>
              <Button
                className={`${classes.primaryButton} ${classes.ctaButton}`}
                component="a"
                href="https://www.enclopedia-official.com/"
                onClick={() =>
                  trackArchiveLpEvent("archive_lp_product_list_click", {
                    position: "final_cta",
                    destination: "home_product_list",
                  })
                }
              >
                商品一覧を見る
              </Button>
              <Button
                className={`${classes.secondaryButton} ${classes.ctaButton}`}
                component="a"
                href="/register?source=archive_fashion_rental"
                onClick={() =>
                  trackArchiveLpEvent("archive_lp_register_click", {
                    position: "final_cta",
                    destination: "register",
                  })
                }
              >
                無料登録する
              </Button>
              <a
                href={lineUrl}
                className={classes.textLink}
                onClick={() =>
                  trackArchiveLpEvent("archive_lp_line_click", {
                    position: "final_cta",
                    destination: "line",
                  })
                }
              >
                LINEで相談する
              </a>
            </div>
          </div>
        </div>
      </Container>
      <div className={classes.fixedCta}>
        <Typography component="p" className={classes.fixedCtaText}>
          気になる商品を選んだ後に無料登録できます
        </Typography>
        <Button
          className={classes.fixedCtaButton}
          component="a"
          href="https://www.enclopedia-official.com/"
          onClick={() =>
            trackArchiveLpEvent("archive_lp_product_list_click", {
              position: "fixed_mobile",
              destination: "home_product_list",
            })
          }
        >
          商品一覧を見る
        </Button>
      </div>
    </div>
  );
}
